import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { CollectionModel, CollectionsDataModel } from './types';

const selectState = (state: RootState): RootState['collections'] => state.collections;

export const selectCollectionById = createSelector(
    [
        (state: RootState) => selectState(state).records,
        (state: RootState, collectionId: string): string => collectionId,
    ],
    (records, collectionId): CollectionsDataModel | undefined => records[collectionId],
);

export const selectCollections = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, namespace: string = 'default') =>
            state.collections?.namespaces[namespace]?.identifiers,
    ],
    (draft, identifiers) =>
        (identifiers || [])
            .map((id: string) => selectCollectionById(draft, id))
            .filter(Boolean) as CollectionModel[],
);

export const selectCreatedCollections = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, namespace: string = 'default') =>
            state.collections?.namespaces[namespace]?.identifiers,
    ],
    (draft, identifiers) =>
        (identifiers || [])
            .map((id: string) => selectCollectionById(draft, id))
            .filter(Boolean)
            .filter((collection) => !collection?.isSaved) as CollectionModel[],
);

export const selectSavedCollections = createSelector(
    [
        (state: RootState) => state,
        (state: RootState, namespace: string = 'default') =>
            state.collections?.namespaces[namespace]?.identifiers,
    ],
    (draft, identifiers) =>
        (identifiers || [])
            .map((id: string) => selectCollectionById(draft, id))
            .filter(Boolean)
            .filter((collection) => collection?.isSaved) as CollectionModel[],
);

export const selectCollectionsLoading = (state: RootState, namespace: string = 'default') =>
    state.collections.namespaces[namespace]?.isLoading ?? false;

export const selectCollectionsError = (state: RootState, namespace: string = 'default') =>
    state.collections.namespaces[namespace]?.hasError ?? false;

export const selectIsCollectionReported = createSelector(
    [selectState, (state: RootState, collectionId: string): string => collectionId],
    (draft, collectionId): boolean => Boolean(draft.reportedCollections[collectionId]),
);

export const selectCollectionsNext = (state: RootState, namespace: string = 'default') =>
    state.collections.namespaces[namespace]?.next;

export const selectCollectionsHasMore = (state: RootState, namespace: string = 'default') =>
    Boolean(state.collections.namespaces[namespace]?.next);

export const selectCollectionsNextSaved = (state: RootState, namespace: string = 'default') =>
    state.collections.namespaces[namespace]?.nextSaved;

export const selectCollectionsHasMoreSaved = (state: RootState, namespace: string = 'default') =>
    Boolean(state.collections.namespaces[namespace]?.nextSaved);

export const selectCollectionsCreatedLoaded = (state: RootState, namespace: string = 'default') =>
    state.collections.namespaces[namespace]?.createdLoaded ?? false;

export const selectCollectionsSavedLoaded = (state: RootState, namespace: string = 'default') =>
    state.collections.namespaces[namespace]?.savedLoaded ?? false;
